import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GUEST_PRIMARY_BUTTON_SX,
  GUEST_TEXT_BUTTON_SX,
  TRACKER_FIELD_SX,
} from './guestTableStyles';
import type { InviteCompose, TrackerHost } from '../../../services/tracker-api-client';

/**
 * Join host display names for prose — "Michael", "Michael & Jonathan". Rows
 * without a display name contribute nothing, and an email address is never a
 * signature. Mirrors the server's JoinHostNames so the prefill matches what an
 * empty override would produce anyway.
 */
export function joinHostDisplayNames(hosts: TrackerHost[]): string {
  const names = [
    ...new Set(
      hosts
        .map((host) => host.displayName?.trim())
        .filter((name): name is string => Boolean(name) && !name!.includes('@')),
    ),
  ];
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

const DEFAULT_NOTE =
  'Hi {name},\n\n' +
  "The plan for {title} is coming together — the schedule, where everyone's " +
  'staying, and your details are all on the trip page.';

export interface SendInviteDialogProps {
  guestName: string;
  /** Prefill for "Signed by" — typically the hosts' display names. */
  defaultSignedBy: string;
  sending: boolean;
  onClose: () => void;
  onSend: (compose: InviteCompose) => void;
}

/**
 * Compose one guest's invite email: the host's note and who it signs as.
 * Defaults are real defaults — sending untouched is exactly what the
 * pre-compose one-click send produced, so the dialog adds control without
 * adding required steps.
 */
export function SendInviteDialog({
  guestName,
  defaultSignedBy,
  sending,
  onClose,
  onSend,
}: SendInviteDialogProps) {
  const [message, setMessage] = useState(DEFAULT_NOTE);
  const [signedBy, setSignedBy] = useState(defaultSignedBy);

  const signedByError = signedBy.includes('@')
    ? 'Sign with a name, not an email address.'
    : signedBy.length > 200
      ? 'Keep the signature under 200 characters.'
      : null;
  const messageError = message.length > 4000 ? 'Keep the note under 4000 characters.' : null;

  const handleSend = () => {
    if (signedByError || messageError) return;
    onSend({
      message: message.trim() || undefined,
      signedBy: signedBy.trim() || undefined,
    });
  };

  return (
    <Dialog
      open
      // Escape and the backdrop are deliberately dead mid-send: closing would
      // leave the host with no idea whether the email went out.
      onClose={sending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          // Dialogs carry the elevation, never an outline as well.
          borderRadius: 'var(--tsb-radius-xl)',
          border: 'none',
          bgcolor: 'var(--tsb-bg-surface)',
          backgroundImage: 'none',
          boxShadow: 'var(--tsb-shadow-3)',
        },
      }}
    >
      <DialogTitle sx={{ color: 'var(--tsb-fg)', fontSize: 17, fontWeight: 700, pb: 1 }}>
        {`Send invite to ${guestName}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Note"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            minRows={5}
            fullWidth
            error={Boolean(messageError)}
            helperText={
              messageError ??
              "{name}, {title}, {location} and {url} fill in per guest. The email's button links to the trip page automatically."
            }
            sx={TRACKER_FIELD_SX}
          />
          <TextField
            label="Signed by"
            value={signedBy}
            onChange={(event) => setSignedBy(event.target.value)}
            fullWidth
            placeholder="e.g. Michael"
            error={Boolean(signedByError)}
            helperText={
              signedByError ??
              "Appears as “— Name” under the note. Leave blank to sign as the trip's hosts."
            }
            sx={TRACKER_FIELD_SX}
          />
          <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
            Guests who already RSVP&rsquo;d elsewhere get this as “A note from…” rather than a fresh
            invitation — nobody is re-invited.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={sending}
          sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || Boolean(signedByError) || Boolean(messageError)}
          sx={GUEST_PRIMARY_BUTTON_SX}
        >
          {sending ? 'Sending…' : 'Send invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SendInviteDialog;
